import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IPRestrictionEntity ,IPTemplateEntity, IPUserExemption} from './ip-access/IPRestrictionInterface';
import { bffInstance } from "../api/axiosInstance";
import { useIPRestrictionStore } from './ip-access/IPRestrictionStore';
import useUserProfileStore from "../core/UserProfileStore";

export interface IUserIPRestrictionEntity{
  entityType:string,
  entityId:string,
  message?:string,
  isRestricted:boolean,
  officeInternalUrl?: string;
  isIpConfigured:boolean
}

type Exemption = {
   exemptedUsers: string[];
   officeInternalUrl?: string;
 };

const getIPRestriction = async (enterpriseId: string, storeId: string) => {
  const res = await bffInstance.get<Record<any, any>[]>(`/getIPRestriction/${enterpriseId}/${storeId}`);
  return res.data;
};

const saveIPRestriction = async (enterpriseId: string, storeId: string, model: any) => {
  const res = await bffInstance.put<Record<any, any>[]>(`/saveIPRestriction/${enterpriseId}/${storeId}`,
    { entityId: model.entityId, 
      allowedIps: model.allowedIps, 
      allowedIpRange: model.allowedIpRange,
      entityType:model.entityType ,
      message: model.message,
      templates: model.templates},
    );
  return res.data;
};

export const fetchUserIPRestriction = async (enterpriseId: string, storeId: string) => {
  const res = await bffInstance.get<IUserIPRestrictionEntity[]>(`/getUserIPRestriction/${enterpriseId}/${storeId}`);
  return res.data;
};


async function checkVpnStatus(url: string, timeoutMs = 3000): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    await fetch(`${url}?_=${Date.now()}`, {
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch (error) {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export function useUserIPRestriction() {
  const userProfile = useUserProfileStore();
  const store = useIPRestrictionStore();
  const defaultStore = userProfile?.iamUserProfile?.fullUser?.stores?.find(
    (s: any) => s.defaultStore === true
  );
  const storeId = defaultStore?.id;

  const query = useQuery({
    queryKey: ["getUserIPRestriction", userProfile.enterpriseId, storeId],
    queryFn: async () => {
      const data = await fetchUserIPRestriction(userProfile.enterpriseId, storeId);
      const url = data?.[0]?.officeInternalUrl;
      if (!url) return data;

      const isConnected = await checkVpnStatus(url);
      return isConnected
        ? data?.map(item =>
            item.isRestricted === true ? { ...item, isRestricted: false } : item
          )
        : data;
    },
    onSuccess(data) {
      store.setIPRestriction(data);
    },
    enabled: !!userProfile.enterpriseId && !!storeId,
  });
    
  return {
    data: store.ipRestriction,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useGetIPTemplate(enterpriseId: string, storeId: string) {
  return useQuery(['getIPTemplate', enterpriseId, storeId], () => getIPTemplate(enterpriseId, storeId),
  { enabled: !!enterpriseId && !!storeId});
}

const getIPTemplate = async (enterpriseId: string, storeId: string) => {
  const res = await bffInstance.get<Record<any, any>[]>(`/getIPTemplate/${enterpriseId}/${storeId}`);
  return res.data as any;
};

export function useGetIPRestriction(enterpriseId: string, storeId: string) {
  return useQuery(['getIPRestriction', enterpriseId, storeId], () => getIPRestriction(enterpriseId, storeId),
  { enabled: !!enterpriseId && !!storeId});
}

const saveIPTemplate = async (enterpriseId: string, storeId: string, model: any) => {
  const res = await bffInstance.put<Record<any, any>[]>(`/saveIPTemplate/${enterpriseId}/${storeId}`,
    { 
      id: model.id, 
      allowedIps: model.allowedIps, 
      allowedIpRange: model.allowedIpRange,
      name:model.name,
      applyByDefault:model.applyByDefault
    },
    );
  return res.data;
};


const saveUserExemption = async (enterpriseId: string, storeId: string, model: any) => {
  const res = await bffInstance.put<Exemption>(`/saveUserExemption/${enterpriseId}/${storeId}`,
      model
   );
  return res.data;
};

const getExemption = async (enterpriseId: string, storeId: string) => {
  const res = await bffInstance.get<Exemption>(`/getExemption/${enterpriseId}/${storeId}`);
  return res.data;
  
};

export function useGetExemption(enterpriseId: string, storeId: string) {

  const {setVpnInternalUrl} = useIPRestrictionStore();
  return useQuery({
    queryKey: ["getExemption", enterpriseId, storeId],
    queryFn: async () => {
      const data = await getExemption(enterpriseId, storeId);
      return data;
    },
    onSuccess(data) {
      setVpnInternalUrl(data?.officeInternalUrl as string);
    },
    enabled: !!enterpriseId && !!storeId,
  });

}

export function useSaveUserExemption({ enterpriseId, storeId, onSuccess, onError }: any) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (model: IPUserExemption) => saveUserExemption(enterpriseId, storeId, model),
    {
      onError: () => {
        onError();
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries(['getExemption']);
      },
    }
  );

  return { mutation };
}

export function useSaveIPTemplate({ enterpriseId, storeId, onSuccess, onError }: any) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (model: IPTemplateEntity) => saveIPTemplate(enterpriseId, storeId, model),
    {
      onError: () => {
        onError();
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries(['getIPTemplate']);
        queryClient.invalidateQueries(['getIPRestriction']);
      },
    }
  );

  return { mutation };
}

export const useSaveIPRestriction = ({ enterpriseId, storeId, onSuccess, onError }: any) => {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (model: IPRestrictionEntity) => saveIPRestriction(enterpriseId, storeId, model),
    {
      onError: () => {
        onError();
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries(['getIPRestriction']);
      },
    }
  );

  return { mutation };
};